import * as THREE from 'three';
import { G, M, TINT, HP, glowMat, mesh, bevel, plate, rib, mergeBones, stages, boneG } from './kit';
/* ============================================================
   SPINNE 蛛 —— BOSS・複合八足
   ============================================================

   剪影：八足撐開・身體極小
   階段：'P1 高站・腿下是陷阱','P2 蹲地・關節進入射程','P3 翻覆・腹部核心朝你（橙）'

   由 `tools/port-bosses.mjs` 從 `enemy_sample_ani.html` 機械移植，
   **不要手改這個檔**：要改造型請改原型再重跑移植器，否則 1:1 就斷了。
   驗證：`node tools/port-bosses.mjs --check`。
   ⚠️ 比對只守得住數字與字串，**動畫對不對只能實看**（圖鑑的開發模式）。 */


/* ── BOSS-12 SPINNE 蛛 ── 複合式：八條腿各自是一段血量，身體小到不成比例。
   P1 高站，腿撐開成一個你可以走進去的空間 —— 進去就被夾。
   P2 蹲到貼地，腿更外張 —— 空間沒了，但腿的關節終於在你打得到的高度。
   P3 整隻翻覆，八條腿朝上亂揮，腹部（真正的核心）朝向你。 */
export function buildSpinne(){
  var GL = glowMat(TINT.red.hex, 2.0), GLA = glowMat(TINT.amb.hex, 2.2);
  var root = new THREE.Group(), legs = [], i;
  var body = boneG(root, 0, 3.20, 0);
  body.add(bevel(M.dark,  2.20, 1.10, 2.60, [0,0,0]));
  body.add(plate(M.armor, 2.40, 0.50, 1.40, [0, 0.44, 0.60], -0.34));
  body.add(plate(M.armor, 2.20, 0.44, 1.20, [0, 0.40,-0.66],  0.30));
  body.add(rib  (M.armor, 2.46, 0.12, 0.12, [0, 0.66, 0.24], -0.34));
  body.add(mesh (G.cone,  M.armor, [1.10,1.40,1.10], [0,-0.70, 0], [Math.PI,0,0]));
  var belly = mesh(G.oct, GLA, [1.20,1.20,1.20], [0,-1.05,0]);
  belly.userData.noMerge = 1; body.add(belly);
  for (i = 0; i < 8; i++){
    var a = (i < 4 ? i*0.42 - 0.63 : (i-4)*0.42 - 0.63 + Math.PI);
    var cx = boneG(body, Math.sin(a)*1.00, -0.10, Math.cos(a)*1.20);
    cx.rotation.y = a;
    cx.add(mesh(G.cyl, M.trim, [0.44,0.42,0.44], [0,0,0], [HP,0,0]));
    var fe = boneG(cx, 0, 0, 0.30);
    fe.add(mesh(G.cyl, M.armor, [0.36,2.40,0.36], [0,0,1.20], [HP,0,0]));
    fe.add(mesh(G.cyl, M.trim,  [0.42,0.34,0.42], [0,0,2.36], [HP,0,0]));
    var ti = boneG(fe, 0, 0, 2.40);
    ti.add(mesh(G.cyl,  M.armor, [0.28,2.60,0.28], [0,0,1.30], [HP,0,0]));
    ti.add(mesh(G.cone, M.trim,  [0.26,0.90,0.26], [0,0,2.90], [HP,0,0]));
    var kg = mesh(G.oct, GL, [0.34,0.34,0.34], [0,0.10,0.06]);
    kg.userData.noMerge = 1; ti.add(kg);
    legs.push({cx:cx, fe:fe, ti:ti, kg:kg, a:a, i:i});
  }
  root.userData.rig = {body:body, legs:legs, belly:belly, GL:GL, GLA:GLA};
  return mergeBones(root);
}
export function updSpinne(rig, o, dt){
  stages(rig, o, dt, 1.8);
  var s1 = rig.s1, s2 = rig.s2, i, L;
  rig.body.position.y = 3.20 - 1.55*s1 - 0.35*s2 + 0.10*Math.sin(o.ph*2*Math.PI * 2)*o.mv;
  rig.body.rotation.z = Math.PI*s2;                  // 三階段整隻翻過來
  rig.body.rotation.x = 0.06*Math.sin(o.ph*Math.PI * 2)*o.mv - o.tel*0.10;
  for (i = 0; i < 8; i++){
    L = rig.legs[i];
    var ph = (o.ph + (i % 2 ? 0.5 : 0) + i*0.06) % 1;
    var step = Math.sin(ph*Math.PI * 2)*o.mv;
    L.cx.rotation.y = L.a + step*0.20 + (s1 + s2)*0.16*(i < 4 ? 1 : -1);
    L.fe.rotation.x = -0.85 + 0.62*s1 + 0.30*s2 - Math.max(0, step)*0.34
                    + s2*0.55*Math.sin(o.t*2.4 + i);
    L.ti.rotation.x =  1.55 - 0.70*s1 - 0.20*s2 + Math.max(0, step)*0.30
                    + s2*0.70*Math.sin(o.t*3.1 + i*2);
    L.kg.scale.setScalar(1 + 0.6*o.tel + 0.4*s1);
  }
  rig.belly.scale.setScalar(0.6 + 0.9*s2 + 0.20*Math.sin(o.t*2.6) + 0.5*o.tel);
  rig.GL.emissiveIntensity  = 2.0 + 2.4*o.tel + 5*o.atk*(1-o.atk);
  rig.GLA.emissiveIntensity = 1.0 + 2.6*s2 + 2.4*o.tel;
}